import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { API_BASE, fallbackPortfolio } from '../data/fallback';

/* ── Helpers ── */
const getEmbedUrl = (url, apiBase) => {
  if (!url || !url.trim()) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&showinfo=0&autoplay=0`;
  // Vimeo
  const vmMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`;
  // Already an embed URL
  if (url.includes('/embed/')) return url;
  // Relative path from server upload — use dedicated streaming route for better seek support
  if (url.startsWith('/uploads/portfolio/videos/')) {
    const filename = url.split('/').pop();
    return `${apiBase}/stream/portfolio/${filename}`;
  }
  // Other relative upload path — prepend API base
  if (url.startsWith('/uploads/')) return `${apiBase}${url}`;
  // Absolute URL (direct mp4/webm/YouTube/Vimeo)
  if (url.startsWith('http')) return url;
  return null;
};

// Determine if a resolved URL is a direct video file (not iframe-embeddable)
const isDirectVideo = (resolvedUrl) => {
  if (!resolvedUrl) return false;
  const lower = resolvedUrl.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg') || lower.endsWith('.mov') || lower.includes('/stream/portfolio/');
};

const PortfolioDetails = () => {
  const { id } = useParams();
  const [project, setProject]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx]       = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [activeVideoIdx, setActiveVideoIdx] = useState(0);

  useEffect(() => {
    setLoading(true);
    setShowVideo(false);
    fetch(`${API_BASE}/api/portfolio/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data.success && data.data) setProject(data.data);
        else {
          const fb = fallbackPortfolio.find(p => p.id === parseInt(id));
          fb ? setProject(fb) : setNotFound(true);
        }
      })
      .catch(() => {
        const fb = fallbackPortfolio.find(p => p.id === parseInt(id));
        fb ? setProject(fb) : setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ padding: '140px 20px', textAlign: 'center', color: '#6c757d', fontSize: '1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:12 }}>
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#6c757d" strokeWidth="2" style={{animation:'spin 0.8s linear infinite'}}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>
      Loading project...
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (notFound || !project) return (
    <div style={{ padding: '140px 20px', textAlign: 'center' }}>
      <h2 style={{ color: '#1a1a5e', marginBottom: 16 }}>Project not found</h2>
      <Link to="/portfolio" className="btn btn-primary">← Back to Portfolio</Link>
    </div>
  );

  const imgSrc = project.image_url
    ? (project.image_url.startsWith('http') ? project.image_url : `${API_BASE}${project.image_url}`)
    : null;

  // Resolve video URL — handles relative /uploads/... paths, YouTube, Vimeo, absolute URLs
  const resolvedVideoUrl = getEmbedUrl(project.video_url, API_BASE);
  const videoIsDirect    = isDirectVideo(resolvedVideoUrl);

  // Multi-video support
  const allVideos = (project.video_urls && project.video_urls.length > 0)
    ? project.video_urls
    : (project.video_url && project.video_url.trim() ? [{ url: project.video_url, label: 'Demo Video' }] : []);
  const hasVideo = allVideos.length > 0;
  const isProduct        = !!project.is_product;
  const features   = Array.isArray(project.features)   ? project.features   : [];
  const tech_stack = Array.isArray(project.tech_stack) ? project.tech_stack : [];
  const hasLink    = project.project_url && project.project_url !== '#';
  const liveUrl    = hasLink
    ? (project.project_url.startsWith('http') ? project.project_url : `https://${project.project_url}`)
    : '#';

  return (
    <div>
      <style>{`
        /* Hero */
        .pdd-hero {
          background: linear-gradient(135deg, #050516 0%, #1a1a5e 60%, #2d2d8e 100%);
          padding: 120px 20px 60px;
          text-align: center;
          color: white;
          position: relative;
          overflow: hidden;
        }
        .pdd-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 60% 40%, rgba(0,212,255,0.08) 0%, transparent 60%);
        }
        .pdd-hero h1 {
          font-size: clamp(1.6rem, 3vw, 2.6rem);
          font-weight: 800;
          margin-bottom: 10px;
          position: relative;
        }
        .pdd-breadcrumb {
          display: flex; justify-content: center;
          gap: 8px; align-items: center;
          font-size: 0.88rem;
          margin-top: 14px;
          color: rgba(255,255,255,0.7);
          position: relative;
        }
        .pdd-breadcrumb a { color: #ff6b35; text-decoration: none; }
        .pdd-breadcrumb a:hover { text-decoration: underline; }

        /* Layout */
        .pdd-layout {
          display: grid;
          grid-template-columns: 1.7fr 1fr;
          gap: 40px;
          align-items: start;
          padding: 70px 0;
        }

        /* Left: media + description */
        .pdd-media-box {
          background: white;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 6px 32px rgba(0,0,0,0.1);
          position: relative;
        }
        .pdd-main-img {
          width: 100%;
          height: 360px;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .pdd-img-placeholder {
          width: 100%; height: 360px;
          display: flex; align-items: center; justify-content: center;
          font-size: 5rem;
        }
        .pdd-video-toggle {
          position: absolute;
          top: 12px; right: 12px;
          background: rgba(255,107,53,0.92);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: pointer;
          display: flex; align-items: center; gap: 6px;
          backdrop-filter: blur(6px);
          transition: all 0.3s ease;
          z-index: 3;
        }
        .pdd-video-toggle:hover { background: #ff6b35; transform: scale(1.04); }

        /* Video embed */
        .pdd-video-wrap {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          height: 0;
          background: #000;
        }
        .pdd-video-wrap iframe,
        .pdd-video-wrap video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        /* Description card */
        .pdd-desc-card {
          background: white;
          border-radius: 18px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.07);
          margin-top: 24px;
        }
        .pdd-section-title {
          font-size: 1.1rem;
          font-weight: 800;
          color: #1a1a5e;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 2px solid #f0f0f0;
          display: flex; align-items: center; gap: 8px;
        }
        .pdd-desc-text {
          color: #6c757d;
          line-height: 1.9;
          font-size: 0.92rem;
          margin-bottom: 0;
        }
        .pdd-features {
          list-style: none;
          padding: 0; margin: 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .pdd-features li {
          display: flex; align-items: flex-start; gap: 8px;
          font-size: 0.88rem; color: #495057;
        }
        .pdd-features li::before {
          content: '✓';
          color: #27ae60;
          font-weight: 800;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .pdd-tech-list {
          display: flex; flex-wrap: wrap; gap: 8px;
        }
        .pdd-tech-badge {
          background: linear-gradient(135deg, #1a1a5e, #2d2d8e);
          color: white;
          padding: 6px 16px;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        /* Right: info sidebar */
        .pdd-info-card {
          background: white;
          border-radius: 18px;
          padding: 28px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.07);
          position: sticky;
          top: 100px;
        }
        .pdd-info-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 18px;
          border-bottom: 2px solid #f0f0f0;
        }
        .pdd-icon-wrap {
          width: 56px; height: 56px;
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.8rem;
          flex-shrink: 0;
        }
        .pdd-info-header-text h2 {
          font-size: 1.1rem;
          font-weight: 800;
          color: #1a1a5e;
          line-height: 1.3;
          margin-bottom: 6px;
        }
        .pdd-info-tags {
          display: flex; gap: 6px; flex-wrap: wrap;
        }
        .pdd-tag {
          padding: 3px 11px;
          border-radius: 50px;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          border: 1.5px solid;
        }
        .pdd-tag-product { color: #10b981; border-color: #10b981; background: rgba(16,185,129,0.08); }
        .pdd-tag-web     { color: #3b82f6; border-color: #3b82f6; background: rgba(59,130,246,0.08); }
        .pdd-tag-app     { color: #8b5cf6; border-color: #8b5cf6; background: rgba(139,92,246,0.08); }
        .pdd-tag-client  { color: #ff6b35; border-color: #ff6b35; background: rgba(255,107,53,0.08); }

        .pdd-info-row {
          margin-bottom: 16px;
        }
        .pdd-info-label {
          font-size: 0.72rem;
          color: #adb5bd;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .pdd-info-value {
          font-size: 0.92rem;
          font-weight: 600;
          color: #1a1a5e;
        }
        .pdd-info-link {
          color: #ff6b35;
          text-decoration: none;
          word-break: break-all;
          font-size: 0.85rem;
        }
        .pdd-info-link:hover { text-decoration: underline; }

        /* Action buttons */
        .pdd-actions { display: flex; flex-direction: column; gap: 10px; margin-top: 22px; }
        .pdd-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 13px 20px;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          border: none;
          width: 100%;
          text-align: center;
        }
        .pdd-btn-primary {
          background: linear-gradient(135deg, #1a1a5e, #2d2d8e);
          color: white;
        }
        .pdd-btn-primary:hover {
          background: linear-gradient(135deg, #ff6b35, #ff8c5a);
          transform: translateY(-2px);
          box-shadow: 0 8px 22px rgba(255,107,53,0.35);
        }
        .pdd-btn-outline {
          background: white;
          color: #1a1a5e;
          border: 2px solid #dee2e6;
        }
        .pdd-btn-outline:hover {
          border-color: #1a1a5e;
          background: #f0f2ff;
          transform: translateY(-2px);
        }
        .pdd-btn-disabled {
          opacity: 0.45;
          cursor: not-allowed;
          pointer-events: none;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .pdd-layout { grid-template-columns: 1fr; }
          .pdd-info-card { position: static; }
        }
        @media (max-width: 768px) {
          .pdd-features { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Hero */}
      <div className="pdd-hero">
        <AnimatedSection>
          <h1>{project.title}</h1>
          <div className="pdd-breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/portfolio">Portfolio</Link>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>{isProduct ? 'Product' : 'Project'} Details</span>
          </div>
        </AnimatedSection>
      </div>

      {/* Content */}
      <section style={{ background: '#f8f9fa' }}>
        <div className="container">
          <div className="pdd-layout">

            {/* ── LEFT: media + description ── */}
            <div>
              <AnimatedSection animation="reveal-left">
                <div className="pdd-media-box" style={{ background: `linear-gradient(135deg, ${project.color || '#3498db'}22, ${project.color || '#3498db'}44)` }}>

                  {/* Video / Image toggle button */}
                  {hasVideo && (
                    <button className="pdd-video-toggle" onClick={() => { setShowVideo(v => !v); setActiveVideoIdx(0); }}>
                      {showVideo ? (
                        <>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          View Image
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          Watch Video{allVideos.length > 1 ? `s (${allVideos.length})` : ''}
                        </>
                      )}
                    </button>
                  )}

                  {showVideo && hasVideo ? (
                    <div>
                      {/* Video tab selector for multiple videos */}
                      {allVideos.length > 1 && (
                        <div style={{ display:'flex', gap:6, padding:'10px 12px', background:'rgba(0,0,0,0.4)', overflowX:'auto' }}>
                          {allVideos.map((v, i) => (
                            <button key={i} type="button"
                              onClick={() => setActiveVideoIdx(i)}
                              style={{
                                padding:'6px 14px', borderRadius:7, border:'1.5px solid',
                                fontSize:'0.78rem', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
                                background:  activeVideoIdx===i ? '#e8603a' : 'rgba(255,255,255,0.07)',
                                borderColor: activeVideoIdx===i ? '#e8603a' : 'rgba(255,255,255,0.2)',
                                color: 'white',
                                transition:'all 0.2s',
                              }}
                            >
                              {v.label || `Video ${i+1}`}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Active video player */}
                      {(() => {
                        const activeVid   = allVideos[activeVideoIdx] || allVideos[0];
                        const resolvedUrl = getEmbedUrl(activeVid.url, API_BASE);
                        const isDirect    = isDirectVideo(resolvedUrl);
                        return (
                          <div className="pdd-video-wrap">
                            {isDirect ? (
                              /* key forces full remount on video switch — critical for src change to work */
                              <video key={`vid-${activeVideoIdx}-${resolvedUrl}`}
                                controls autoPlay={false} playsInline
                                style={{ position:'absolute', inset:0, width:'100%', height:'100%', background:'#000' }}>
                                <source src={resolvedUrl} type="video/mp4" />
                                <source src={resolvedUrl} type="video/webm" />
                              </video>
                            ) : (
                              /* key forces iframe reload on tab switch */
                              <iframe key={`iframe-${activeVideoIdx}-${resolvedUrl}`}
                                src={resolvedUrl}
                                title={activeVid.label || 'Demo Video'}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen />
                            )}
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : imgSrc ? (
                    <img
                      className="pdd-main-img"
                      src={imgSrc}
                      alt={project.title}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="pdd-img-placeholder">
                      {project.icon && project.icon.startsWith('/uploads/') ? (
                        <img src={`${API_BASE}${project.icon}`} alt="icon" style={{ width:80, height:80, objectFit:'cover', borderRadius:16 }} onError={e=>e.target.style.display='none'}/>
                      ) : (
                        <span>{project.icon || '🖥️'}</span>
                      )}
                    </div>
                  )}
                </div>
              </AnimatedSection>

              <AnimatedSection animation="reveal-left" delay={80}>
                <div className="pdd-desc-card">
                  {/* Overview */}
                  <div className="pdd-section-title">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#1a1a5e" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    Project Overview
                  </div>
                  <p className="pdd-desc-text">{project.description || 'No description available.'}</p>

                  {/* Features */}
                  {features.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div className="pdd-section-title">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#1a1a5e" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        Key Features
                      </div>
                      <ul className="pdd-features">
                        {features.map((f, i) => (
                          <li key={i}>{typeof f === 'object' ? f.text : f}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tech Stack */}
                  {tech_stack.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div className="pdd-section-title">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#1a1a5e" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                        Technologies Used
                      </div>
                      <div className="pdd-tech-list">
                        {tech_stack.map((t, i) => (
                          <span key={i} className="pdd-tech-badge">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </AnimatedSection>
            </div>

            {/* ── RIGHT: info sidebar ── */}
            <AnimatedSection animation="reveal-right">
              <div className="pdd-info-card">
                {/* Header */}
                <div className="pdd-info-header">
                  <div
                    className="pdd-icon-wrap"
                    style={{ background: `${project.color || '#3498db'}22`, overflow:'hidden' }}
                  >
                    {project.icon && project.icon.startsWith('/uploads/') ? (
                      <img src={`${API_BASE}${project.icon}`} alt="icon" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e=>e.target.style.display='none'}/>
                    ) : (
                      <span style={{ fontSize:'1.8rem' }}>{project.icon || '🖥️'}</span>
                    )}
                  </div>
                  <div className="pdd-info-header-text">
                    <h2>{project.title}</h2>
                    <div className="pdd-info-tags">
                      {isProduct && <span className="pdd-tag pdd-tag-product">Product</span>}
                      {!isProduct && <span className="pdd-tag pdd-tag-client">Client Project</span>}
                      {project.category === 'BOTH' ? (
                        <>
                          <span className="pdd-tag pdd-tag-web">Website</span>
                          <span className="pdd-tag pdd-tag-app">App</span>
                        </>
                      ) : (
                        <span className={`pdd-tag ${project.category === 'APP' ? 'pdd-tag-app' : 'pdd-tag-web'}`}>
                          {project.category === 'APP' ? 'Mobile App' : 'Website'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info rows */}
                {[
                  // { label: 'Client',        value: project.client       || '—' },
                  { label: 'Project Date',  value: project.project_date || '—' },
                  { label: 'Category',      value: project.category     || '—' },
                  { label: 'Type',          value: isProduct ? 'Our Product' : 'Client Project' },
                ].map(({ label, value }) => (
                  <div key={label} className="pdd-info-row">
                    <div className="pdd-info-label">{label}</div>
                    <div className="pdd-info-value">{value}</div>
                  </div>
                ))}



                {/* Actions */}
                <div className="pdd-actions">
                  <a
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`pdd-btn pdd-btn-primary${!hasLink ? ' pdd-btn-disabled' : ''}`}
                    onClick={!hasLink ? e => e.preventDefault() : undefined}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    View {isProduct ? 'Product' : project.category === 'BOTH' ? 'Website / App' : project.category === 'APP' ? 'App' : 'Website'}
                  </a>

                  {hasVideo && (
                    <button
                      className="pdd-btn pdd-btn-outline"
                      onClick={() => { setShowVideo(v => !v); if (!showVideo) setActiveVideoIdx(0); }}
                    >
                      {showVideo ? (
                        <>
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          View Screenshot
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" style={{flexShrink:0}}><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          Watch Demo Video{allVideos.length > 1 ? ` (${allVideos.length})` : ''}
                        </>
                      )}
                    </button>
                  )}

                  <Link to="/portfolio" className="pdd-btn pdd-btn-outline">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                    Back to Portfolio
                  </Link>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioDetails;
